import { useHomeTabs } from '../../queries/useHomeTabs';
import { TabView } from 'react-native-tab-view';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Typography, useScreenSize } from '@banhala/ui-kit';
import { useSetAtom } from 'jotai';
import {
  activeHomeTabAtom,
  type ActiveHomeTab,
} from '../../states/homeTab.atom';
import type { HomeTabItem } from 'shared/src/api';
import { HomeAtomic } from '../HomeAtomic';
import { HomeWebView } from '../HomeWebView';
import { TodayAtomic } from '../TodayAtomic';
import { GoodsRanking } from '../GoodsRanking';
import { MarketRanking } from '../MarketRanking';
import { HomeTabBar } from './components/HomeTabBar';
import { isWeb } from 'shared/src/utils';
import { useLogTabNavigation } from '../../hooks/useLogTabNavigation';
import { useHomeRoute } from '../../../../hooks/useHomeRoute';
import { useHomeNavigation } from '../../../../hooks/useHomeNavigation';

export type HomeTabRoute = {
  key: string;
  title: string;
} & HomeTabItem;

export const HomeContents = () => {
  const { data: homeTabs } = useHomeTabs();
  const route = useHomeRoute<'Home'>();
  const navigation = useHomeNavigation<'Home'>();
  const size = useScreenSize();
  const setActiveHomeTab = useSetAtom(activeHomeTabAtom);

  const routes = useMemo<HomeTabRoute[]>(() => {
    return (
      homeTabs.item_list?.map(item => ({
        ...item,
        key: item.id,
        title: item.name,
      })) ?? []
    );
  }, [homeTabs.item_list]);

  const selectedRaw = route.params?.selected;
  const index = Math.max(
    selectedRaw != null
      ? routes.findIndex(
          item => item.id === selectedRaw || item.screen_name === selectedRaw,
        )
      : routes.findIndex(item => item.id === homeTabs.selected_id),
    0,
  );

  // 현재 index + routes로부터 활성 탭 메타 도출 → atom 동기화
  const activeTab = useMemo<ActiveHomeTab | null>(() => {
    const current = routes[index];
    if (!current) return null;
    return {
      id: current.id,
      screen_name: current.screen_name,
      type: current.type,
      name: current.name,
    };
  }, [routes, index]);

  useEffect(() => {
    setActiveHomeTab(activeTab);
  }, [activeTab, setActiveHomeTab]);

  const indexRef = useRef(index);
  indexRef.current = index;
  const isTabClickRef = useRef(false);
  const { logClickUpperTab, logSwipeTabNavigation } =
    useLogTabNavigation(routes);

  const handleTabClick = useCallback(
    (nextIndex: number) => {
      isTabClickRef.current = true;
      logClickUpperTab(indexRef.current, nextIndex);
    },
    [logClickUpperTab],
  );

  const handleIndexChange = useCallback(
    (nextIndex: number) => {
      if (!isTabClickRef.current) {
        logSwipeTabNavigation(indexRef.current, nextIndex);
      }
      isTabClickRef.current = false;
      const nextId = routes[nextIndex]?.id;
      if (nextId) {
        navigation.setParams({ selected: nextId });
      }
    },
    [logSwipeTabNavigation, navigation, routes],
  );

  if (homeTabs.item_list.length === 0) return null;

  return (
    <TabView<HomeTabRoute>
      lazy={true}
      navigationState={{ index, routes }}
      renderScene={item => {
        if (item.route.type === 'ATOMIC') {
          return (
            <HomeAtomic
              screenName={item.route.screen_name}
              nextToken={item.route.default_query?.next_token}
            />
          );
        }

        if (item.route.type === 'WEBVIEW') {
          return <HomeWebView url={item.route.default_query?.url} />;
        }

        if (item.route.type === 'CUSTOM') {
          if (item.route.screen_name === 'TODAY') {
            return <TodayAtomic screenName={item.route.screen_name} />;
          }

          if (item.route.screen_name === 'GOODS_RANKING') {
            return <GoodsRanking />;
          }

          if (item.route.screen_name === 'MARKET_RANKING') {
            return <MarketRanking />;
          }
        }
        return <Typography>{item.route.title}</Typography>;
      }}
      onIndexChange={handleIndexChange}
      initialLayout={{
        width: size.width,
      }}
      swipeEnabled={!isWeb}
      renderTabBar={props => (
        <HomeTabBar {...props} onTabClick={handleTabClick} />
      )}
    />
  );
};
